import { useState, useEffect, useRef } from 'react';
import {
  usePagedList,
  listParams,
  useStickyOptions,
  useStickyRow,
} from '../utils/pagedList';
import { usePermissions } from '@/common/hooks/usePermissions';
import { PERMISSIONS } from '@/common/config/permissions';
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { decodeId } from '@/common/utils/encryption';
import toast from 'react-hot-toast';
import {
  Button,
  ConfirmDeleteModal,
  CustomSelect,
  CustomDateRangePicker,
  SearchInput,
  SideDrawer,
  SkeletonText,
} from '@/components';
import { PermissionGuard, V2Checkbox, V2Table } from '@/common/components';
import { CRMContactForm } from '../components/CRMContactForm';
import { CRMImportWizard } from '../components/CRMImportWizard';
import { CRMExportModal } from '../components/CRMExportModal';
import useBodyScrollLock from '@/common/hooks/useBodyScrollLock';
import {
  useDelayedSkeleton,
  useBulkSelection,
  useMobileScrollLock,
  useOutsideClick,
} from '@/common/hooks';
import { parseApiError, formatConsentStatus } from '@/common/utils';
import { env } from '@/common/config';
import { apiClient } from '@/common/lib/api-client';
import {
  createCRMContact,
  getCRMContacts,
  updateCRMContact,
  deleteCRMContact,
  bulkDeleteCRMContacts,
  createCRMContactSource,
  getCRMPipelineStages,
  exportCRMContacts,
  getCRMContactDetail,
  getCRMTags,
  getCRMCustomFields,
} from '../api';
import { getCRMSourcesStats, getCompanyEmployees } from '../../api';
import { getWhatsAppFlows } from '../../whatsapp-flows/api';
import { getDisplayPhone } from '../utils/phoneUtils';
import {
  buildCRMContactsListParams,
  buildCRMContactsExportFilterParams,
} from '../utils/filterParams';

import { PlusIcon, FilterIcon } from '../../components/Icons';
import importIcon from '../../assets/importIcon.png';
import exportIcon from '../../assets/exportIcon.png';
import editIcon from '../../assets/editIcon.png';
import trashIcon from '../../assets/trashIcon.png';
import datePickerCalendarIcon from '../../assets/Inquiry/calendar.png';
import {
  DashboardPage,
  TableChip,
  getConsentChipTone,
} from '../../components/PremiumDesign';

const LEADS_PAGE_SIZE = 10;

export function CRMContactsPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [isSourceModalOpen, setIsSourceModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const getInitialFilters = () => {
    try {
      const saved = sessionStorage.getItem('crm-contacts-filters');
      if (saved) return JSON.parse(saved);
    } catch {
      // ignore
    }
    return {};
  };

  const [initialFilters] = useState(getInitialFilters);

  const location = useLocation();
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [contactsToDeleteBulk, setContactsToDeleteBulk] = useState(false);
  const [searchQuery, setSearchQuery] = useState(
    initialFilters.searchQuery || '',
  );

  const {
    selectedIds: selectedContacts,
    selectMode,
    excludedIds,
    toggleSelection,
    clearSelection,
    isSelected,
    headerChecked,
    toggleSelectAll,
    selectedCountFor,
  } = useBulkSelection();
  const [debouncedSearch, setDebouncedSearch] = useState(
    initialFilters.debouncedSearch || '',
  );
  const [apiErrors, setApiErrors] = useState({});
  const [stageFilter, setStageFilter] = useState(
    initialFilters.stageFilter || initialFilters.selectedStage || '',
  );
  const [sourceFilter, setSourceFilter] = useState(
    initialFilters.sourceFilter || initialFilters.selectedSource || '',
  );
  const [tagFilter, setTagFilter] = useState(
    initialFilters.tagFilter || initialFilters.selectedTag || '',
  );
  const [dateFrom, setDateFrom] = useState(initialFilters.dateFrom || '');
  const [dateTo, setDateTo] = useState(initialFilters.dateTo || '');
  const [valueMin, setValueMin] = useState(initialFilters.valueMin || '');
  const [valueMax, setValueMax] = useState(initialFilters.valueMax || '');
  const [debouncedValueMin, setDebouncedValueMin] = useState(
    initialFilters.debouncedValueMin ?? initialFilters.valueMin ?? '',
  );
  const [debouncedValueMax, setDebouncedValueMax] = useState(
    initialFilters.debouncedValueMax ?? initialFilters.valueMax ?? '',
  );
  const [consentFilter, setConsentFilter] = useState(
    initialFilters.consentFilter || '',
  );
  const [assignedFilter, setAssignedFilter] = useState(
    initialFilters.assignedFilter || '',
  );
  const [flowFilter, setFlowFilter] = useState(
    initialFilters.flowFilter || initialFilters.selectedFlow || '',
  );
  const [customFieldSlug, setCustomFieldSlug] = useState(
    initialFilters.customFieldSlug || '',
  );
  const [customFieldValue, setCustomFieldValue] = useState(
    initialFilters.customFieldValue || '',
  );
  const [debouncedCustomFieldValue, setDebouncedCustomFieldValue] = useState(
    initialFilters.debouncedCustomFieldValue ??
      initialFilters.customFieldValue ??
      '',
  );
  const [activeFilterFields, setActiveFilterFields] = useState(() => {
    if (initialFilters.activeFilterFields?.length) {
      return initialFilters.activeFilterFields;
    }
    // Migrate older session filters (consent / stage / source / tag only)
    const fields = [];
    if (initialFilters.consentFilter) fields.push('consent');
    if (initialFilters.stageFilter || initialFilters.selectedStage) {
      fields.push('stage');
    }
    if (initialFilters.sourceFilter || initialFilters.selectedSource) {
      fields.push('source');
    }
    if (initialFilters.tagFilter || initialFilters.selectedTag) {
      fields.push('tag');
    }
    if (initialFilters.flowFilter || initialFilters.selectedFlow) {
      fields.push('automation');
    }
    return fields;
  });
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const filterMenuRef = useRef(null);

  useMobileScrollLock(isFilterMenuOpen);
  const [page, setPage] = useState(initialFilters.page || 1);

  const isInitialSearchMount = useRef(true);
  const isInitialFilterMount = useRef(true);
  const [highlightedContactId, setHighlightedContactId] = useState(null);

  useEffect(() => {
    sessionStorage.setItem(
      'crm-contacts-filters',
      JSON.stringify({
        searchQuery,
        debouncedSearch,
        stageFilter,
        sourceFilter,
        tagFilter,
        dateFrom,
        dateTo,
        valueMin,
        valueMax,
        debouncedValueMin,
        debouncedValueMax,
        consentFilter,
        assignedFilter,
        flowFilter,
        customFieldSlug,
        customFieldValue,
        debouncedCustomFieldValue,
        activeFilterFields,
        page,
      }),
    );
  }, [
    searchQuery,
    debouncedSearch,
    stageFilter,
    sourceFilter,
    tagFilter,
    dateFrom,
    dateTo,
    valueMin,
    valueMax,
    debouncedValueMin,
    debouncedValueMax,
    consentFilter,
    assignedFilter,
    flowFilter,
    customFieldSlug,
    customFieldValue,
    debouncedCustomFieldValue,
    activeFilterFields,
    page,
  ]);

  useEffect(() => {
    if (isInitialSearchMount.current) {
      isInitialSearchMount.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValueMin(valueMin);
      setDebouncedValueMax(valueMax);
    }, 500);
    return () => clearTimeout(timer);
  }, [valueMin, valueMax]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCustomFieldValue(customFieldValue);
    }, 500);
    return () => clearTimeout(timer);
  }, [customFieldValue]);

  useEffect(() => {
    if (isInitialFilterMount.current) {
      isInitialFilterMount.current = false;
      return;
    }
    setPage(1);
  }, [
    stageFilter,
    sourceFilter,
    tagFilter,
    dateFrom,
    dateTo,
    debouncedValueMin,
    debouncedValueMax,
    consentFilter,
    assignedFilter,
    flowFilter,
    customFieldSlug,
    debouncedCustomFieldValue,
  ]);

  // Highlight a row when coming from a notification
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const encodedId = searchParams.get('contact_id');
    if (!encodedId) return;
    const decoded = decodeId(encodedId);
    const leadId =
      decoded && !isNaN(Number(decoded))
        ? Number(decoded)
        : !isNaN(Number(encodedId))
          ? Number(encodedId)
          : null;
    if (!leadId) return;
    setHighlightedContactId(leadId);
    const timer = setTimeout(() => setHighlightedContactId(null), 8000);
    return () => clearTimeout(timer);
  }, [location.search]);

  // Scroll highlighted row into view once contacts load
  useEffect(() => {
    if (!highlightedContactId) return;
    const timer = setTimeout(() => {
      const el = document.getElementById(`contact-row-${highlightedContactId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 400);
    return () => clearTimeout(timer);
  }, [highlightedContactId]);

  useOutsideClick(
    filterMenuRef,
    () => setIsFilterMenuOpen(false),
    isFilterMenuOpen,
  );

  let currentCompanyId =
    localStorage.getItem('company_id') || sessionStorage.getItem('company_id');
  if (!currentCompanyId) {
    try {
      const userStr =
        localStorage.getItem('user') || sessionStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        currentCompanyId = user?.company_id || user?.company?.id || user?.id;
      }
    } catch {
      // ignore parsing error
    }
  }

  const queryClient = useQueryClient();

  const filterState = {
    search: debouncedSearch,
    stageId: stageFilter,
    source: sourceFilter,
    tag: tagFilter,
    dateFrom,
    dateTo,
    valueMin: debouncedValueMin,
    valueMax: debouncedValueMax,
    consentStatus: consentFilter,
    assignedEmployeeId: assignedFilter,
    flowId: flowFilter,
    customFieldSlug,
    customFieldValue: debouncedCustomFieldValue,
  };

  const {
    data: contactsData,
    isLoading: isContactsLoading,
    isFetching: isContactsFetching,
    isError: isContactsError,
    error: contactsError,
  } = useQuery({
    queryKey: [
      'crm-contacts',
      currentCompanyId,
      debouncedSearch,
      page,
      stageFilter,
      sourceFilter,
      tagFilter,
      dateFrom,
      dateTo,
      debouncedValueMin,
      debouncedValueMax,
      consentFilter,
      assignedFilter,
      flowFilter,
      customFieldSlug,
      debouncedCustomFieldValue,
    ],
    queryFn: async () => {
      if (!currentCompanyId) return { results: [] };
      const params = buildCRMContactsListParams(filterState);
      if (page > 1) params.page = page;
      return getCRMContacts(currentCompanyId, params);
    },
    enabled: !!currentCompanyId,
    placeholderData: keepPreviousData,
  });

  const { data: stagesData } = useQuery({
    queryKey: ['crm-stages', currentCompanyId],
    queryFn: async () => {
      if (!currentCompanyId) return [];
      const res = await getCRMPipelineStages(currentCompanyId);
      return res?.results || res || [];
    },
    enabled: !!currentCompanyId,
  });

  const { data: sourcesData } = useQuery({
    queryKey: ['crm-sources-stats', currentCompanyId],
    queryFn: () => getCRMSourcesStats(currentCompanyId),
    enabled: !!currentCompanyId,
  });

  const tagsList = usePagedList({
    queryKey: ['crm-tags', currentCompanyId, 'leads-filter'],
    enabled: !!currentCompanyId,
    idOf: (row) => row.id,
    queryFn: ({ page, search }) =>
      getCRMTags(currentCompanyId, listParams(page, search)),
  });
  const employeesList = usePagedList({
    queryKey: ['companyEmployees', currentCompanyId, 'leads-filter'],
    enabled: !!currentCompanyId,
    idOf: (row) => row.id,
    queryFn: ({ page, search }) =>
      getCompanyEmployees(currentCompanyId, listParams(page, search)),
  });
  const flowsList = usePagedList({
    queryKey: ['whatsapp-flows', currentCompanyId, 'leads-filter', 'published'],
    enabled: !!currentCompanyId,
    idOf: (row) => row.id,
    queryFn: ({ page, search }) =>
      getWhatsAppFlows(currentCompanyId, {
        status: 'published',
        ...listParams(page, search),
      }),
  });
  const customFieldsList = usePagedList({
    queryKey: ['crm-custom-fields', currentCompanyId, 'leads-filter'],
    enabled: !!currentCompanyId,
    idOf: (row) => row.slug ?? row.id,
    queryFn: ({ page, search }) =>
      getCRMCustomFields(currentCompanyId, listParams(page, search)),
  });
  const tagsData = tagsList.rows;
  const employeesData = employeesList.rows;
  const flowsData = flowsList.rows;
  const customFieldsData = customFieldsList.rows;

  const stageOptions = (stagesData || []).map((s) => ({
    value: s.id,
    label: s.name,
  }));
  const sourceOptions =
    sourcesData?.sources?.map((src) => ({
      value: src.source_type,
      label: src.source_type,
    })) || [];
  const tagOptions = useStickyOptions(
    (tagsData || []).map((t) => ({
      value: t.name,
      label: t.name,
    })),
    tagFilter,
  );
  const tagIdByName = (tagsData || []).reduce((acc, t) => {
    if (t?.name != null && t?.id != null) acc[t.name] = t.id;
    return acc;
  }, {});
  const employeeOptions = useStickyOptions(
    (employeesData || []).map((emp) => ({
      value: emp.id,
      label: emp.name || emp.full_name || emp.email || `User #${emp.id}`,
    })),
    assignedFilter,
  );
  const flowOptions = useStickyOptions(
    (Array.isArray(flowsData) ? flowsData : [])
      .filter((flow) => !flow.is_draft && flow.status !== 'draft')
      .map((flow) => ({
        value: String(flow.id),
        label: flow.name || `Flow #${flow.id}`,
      })),
    flowFilter,
  );
  const customFieldOptions = useStickyOptions(
    (customFieldsData || []).map((f) => ({
      value: f.slug,
      label: f.name,
    })),
    customFieldSlug,
  );
  const selectedCustomField = useStickyRow(
    customFieldsData,
    customFieldSlug,
    (f) => f.slug,
  );

  const contacts = contactsData?.results || [];
  const count = contactsData?.count || 0;
  const selectedCount = selectedCountFor(count);

  const showDelayedSkeleton = useDelayedSkeleton(
    isContactsFetching && contactsData !== undefined,
    300,
    500,
  );

  const isLoading = isContactsLoading || showDelayedSkeleton;
  const filteredContacts = contacts;

  const buildListParams = () => buildCRMContactsListParams(filterState);

  useEffect(() => {
    clearSelection();
  }, [
    clearSelection,
    debouncedSearch,
    stageFilter,
    sourceFilter,
    tagFilter,
    dateFrom,
    dateTo,
    debouncedValueMin,
    debouncedValueMax,
    consentFilter,
    assignedFilter,
    flowFilter,
    customFieldSlug,
    debouncedCustomFieldValue,
  ]);

  const handleClearAllFilters = () => {
    setStageFilter('');
    setSourceFilter('');
    setTagFilter('');
    setDateFrom('');
    setDateTo('');
    setValueMin('');
    setValueMax('');
    setDebouncedValueMin('');
    setDebouncedValueMax('');
    setConsentFilter('');
    setAssignedFilter('');
    setFlowFilter('');
    setCustomFieldSlug('');
    setCustomFieldValue('');
    setDebouncedCustomFieldValue('');
    setActiveFilterFields([]);
  };

  const handleRemoveFilterField = (key) => {
    setActiveFilterFields((prev) => prev.filter((f) => f !== key));
    if (key === 'stage') setStageFilter('');
    if (key === 'source') setSourceFilter('');
    if (key === 'tag') setTagFilter('');
    if (key === 'createDate') {
      setDateFrom('');
      setDateTo('');
    }
    if (key === 'estimatedValue') {
      setValueMin('');
      setValueMax('');
      setDebouncedValueMin('');
      setDebouncedValueMax('');
    }
    if (key === 'consent') setConsentFilter('');
    if (key === 'assigned') setAssignedFilter('');
    if (key === 'automation') setFlowFilter('');
    if (key === 'customField') {
      setCustomFieldSlug('');
      setCustomFieldValue('');
      setDebouncedCustomFieldValue('');
    }
  };

  const handleAddFilterField = (key) => {
    setActiveFilterFields((prev) =>
      prev.includes(key) ? prev : [...prev, key],
    );
    setIsFilterMenuOpen(false);
  };

  const FILTER_FIELDS = [
    {
      key: 'stage',
      label: 'Stage',
      isSet: !!stageFilter,
      onClear: () => setStageFilter(''),
    },
    {
      key: 'source',
      label: 'Source',
      isSet: !!sourceFilter,
      onClear: () => setSourceFilter(''),
    },
    {
      key: 'tag',
      label: 'Tag',
      isSet: !!tagFilter,
      onClear: () => setTagFilter(''),
    },
    {
      key: 'createDate',
      label: 'Create Date',
      isSet: !!(dateFrom || dateTo),
      onClear: () => {
        setDateFrom('');
        setDateTo('');
      },
    },
    {
      key: 'estimatedValue',
      label: 'Estimated Value',
      isSet: !!(valueMin || valueMax),
      onClear: () => {
        setValueMin('');
        setValueMax('');
      },
    },
    {
      key: 'assigned',
      label: 'Assigned Employee',
      isSet: !!assignedFilter,
      onClear: () => setAssignedFilter(''),
    },
    {
      key: 'consent',
      label: 'Consent Status',
      isSet: !!consentFilter,
      onClear: () => setConsentFilter(''),
    },
    {
      key: 'automation',
      label: 'Stage Triggers',
      isSet: !!flowFilter,
      onClear: () => setFlowFilter(''),
    },
    {
      key: 'customField',
      label: 'Custom Field',
      isSet: !!(customFieldSlug && customFieldValue !== ''),
      onClear: () => {
        setCustomFieldSlug('');
        setCustomFieldValue('');
      },
    },
  ];

  const handleToggleSelectAll = () => {
    toggleSelectAll({ currentPageItems: filteredContacts });
  };

  const buildDeleteSelection = () => {
    const params = buildListParams();
    let customFields = {};
    if (params.custom_fields) {
      try {
        customFields = JSON.parse(params.custom_fields);
      } catch {
        customFields = {};
      }
    }
    return {
      mode: 'all_matching',
      excluded_ids: Array.from(excludedIds),
      search: params.search || '',
      from_date: params.from_date || null,
      to_date: params.to_date || null,
      tags: params.tags || '',
      sources: params.sources || '',
      stage_ids: params.stage_ids || '',
      consent_status: params.consent_status || '',
      assigned_to_id: null,
      assigned_to_employee_id: params.assigned_to_employee_id ?? null,
      segment: null,
      flow_id: params.flow_id ?? null,
      min_estimated_value:
        params.min_estimated_value != null ? params.min_estimated_value : '',
      max_estimated_value:
        params.max_estimated_value != null ? params.max_estimated_value : '',
      custom_fields: customFields,
    };
  };

  const { mutate: executeBatchDelete, isPending: isDeletingBatch } =
    useMutation({
      mutationFn: () => {
        if (!can(PERMISSIONS.LEADS.DELETE)) {
          toast.error('You do not have permission to perform this action.');
          return Promise.reject(new Error('Unauthorized'));
        }
        if (selectMode === 'all_matching') {
          return bulkDeleteCRMContacts({
            companyId: currentCompanyId,
            selection: buildDeleteSelection(),
          });
        }
        return bulkDeleteCRMContacts({
          companyId: currentCompanyId,
          contactIds: Array.from(selectedContacts),
        });
      },
      onSuccess: (data) => {
        const deletedCount = data?.deleted_count ?? selectedCount;
        toast.success(`Successfully deleted ${deletedCount} lead(s).`);

        const pageSize = LEADS_PAGE_SIZE;
        const remaining = Math.max(0, (count || 0) - deletedCount);
        const maxPage = Math.max(1, Math.ceil(remaining / pageSize));
        const nextPage = Math.min(page, maxPage);
        const pageChanged = nextPage !== page;

        clearSelection();
        setContactsToDeleteBulk(false);
        if (pageChanged) setPage(nextPage);

        queryClient.invalidateQueries({
          queryKey: ['crm-contacts', currentCompanyId],
          refetchType: pageChanged ? 'none' : 'active',
        });
      },
      onError: (err) => {
        const errorData = err.response?.data;
        toast.error(
          errorData?.message ||
            errorData?.detail ||
            'Failed to delete selected lead(s).',
        );
      },
    });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    const datePart = d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    const timePart = d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return `${datePart}, ${timePart}`;
  };

  const { mutate: handleCreateContact, isPending: isCreating } = useMutation({
    mutationFn: createCRMContact,
    onSuccess: () => {
      toast.success('Lead created successfully!');
      setApiErrors({});
      setIsModalOpen(false);
      queryClient.invalidateQueries({
        queryKey: ['crm-contacts', currentCompanyId],
      });
    },
    onError: (err) => {
      const { message, fieldErrors } = parseApiError(err);
      setApiErrors(fieldErrors);
      toast.error(message || 'Failed to create lead.');
    },
  });

  const { mutate: handleUpdateContact, isPending: isUpdating } = useMutation({
    mutationFn: updateCRMContact,
    onSuccess: () => {
      toast.success('Lead updated successfully!');
      setApiErrors({});
      setIsModalOpen(false);
      queryClient.invalidateQueries({
        queryKey: ['crm-contacts', currentCompanyId],
      });
    },
    onError: (err) => {
      const { message, fieldErrors } = parseApiError(err);
      setApiErrors(fieldErrors);
      toast.error(message || 'Failed to update lead.');
    },
  });

  const { mutate: executeDeleteContact } = useMutation({
    mutationFn: deleteCRMContact,
    onSuccess: () => {
      toast.success('Lead deleted successfully!');
      queryClient.invalidateQueries({
        queryKey: ['crm-contacts', currentCompanyId],
      });
    },
    onError: (err) => {
      const errorData = err.response?.data;
      toast.error(
        errorData?.message || errorData?.detail || 'Failed to delete lead.',
      );
    },
  });

  const { mutate: handleCreateSource, isPending: isCreatingSource } =
    useMutation({
      mutationFn: createCRMContactSource,
      onSuccess: () => {
        toast.success('Source created successfully!');
        setIsSourceModalOpen(false);
      },
      onError: (err) => {
        const errorData = err.response?.data;
        toast.error(
          errorData?.message || errorData?.detail || 'Failed to create source.',
        );
      },
    });

  const { mutate: handleExport, isPending: isExporting } = useMutation({
    mutationFn: exportCRMContacts,
    onSuccess: async (data) => {
      if (data?.headers && data?.data) {
        toast.success(`Export successful! ${data.data.length} leads exported.`);

        const headers = data.headers;
        const csvRows = [];
        csvRows.push(
          headers
            .map((header) => `"${String(header).replace(/"/g, '""')}"`)
            .join(','),
        );

        for (const row of data.data) {
          const values = headers.map((header) => {
            const val =
              row[header] === null || row[header] === undefined
                ? ''
                : row[header];
            return `"${String(val).replace(/"/g, '""')}"`;
          });
          csvRows.push(values.join(','));
        }

        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', data.file_name || 'contacts_export.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } else if (data?.download_url) {
        toast.success(
          `Export successful! ${data.exported_count || ''} leads exported.`,
        );

        let url = data.download_url;
        if (!url.startsWith('http')) {
          try {
            const apiOrigin = new URL(
              env.apiBaseUrl || '/',
              window.location.origin,
            ).origin;
            url = `${apiOrigin}/${url.replace(/^\//, '')}`;
          } catch {
            url = `${env.apiBaseUrl.replace(/\/api\/?$/, '')}/${url.replace(/^\//, '')}`;
          }
        }

        try {
          // Attempt to fetch as blob to enforce download
          const response = await apiClient.get(url, { responseType: 'blob' });
          const blob = new Blob([response.data]);
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.setAttribute(
            'download',
            data.file_name || 'contacts_export.csv',
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
          // Fallback to direct link if CORS or other issues block the blob fetch
          console.warn(
            'Blob fetch failed, falling back to direct download:',
            error,
          );
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute(
            'download',
            data.file_name || 'contacts_export.csv',
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        toast.success('Export started! Check jobs or email for progress.');
      }
    },
    onError: (err) => {
      const errorData = err.response?.data;
      toast.error(
        errorData?.message || errorData?.detail || 'Failed to trigger export.',
      );
    },
  });

  const onExportClick = () => {
    setIsExportModalOpen(true);
  };

  const onExportSubmit = (paramsObj) => {
    const { include_notes, include_followups } = paramsObj;
    const listParams = buildListParams();
    handleExport({
      companyId: currentCompanyId,
      payload: {
        include_notes,
        include_followups,
        filter_params: buildCRMContactsExportFilterParams(listParams, {
          tagIdByName,
        }),
      },
    });
    setIsExportModalOpen(false);
  };

  const handleDelete = (contactId) => {
    setContactToDelete(contactId);
  };

  const isPending = isCreating || isUpdating;

  const isAnyModalOpen =
    isModalOpen ||
    isSourceModalOpen ||
    isAssignModalOpen ||
    isImportModalOpen ||
    isExportModalOpen ||
    !!contactToDelete ||
    contactsToDeleteBulk;

  useBodyScrollLock(isAnyModalOpen);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (isModalOpen && !isPending) setIsModalOpen(false);
        if (isSourceModalOpen && !isCreatingSource) setIsSourceModalOpen(false);
        if (isAssignModalOpen) setIsAssignModalOpen(false);
        if (isImportModalOpen) setIsImportModalOpen(false);
        if (contactToDelete) setContactToDelete(null);
        if (contactsToDeleteBulk && !isDeletingBatch)
          setContactsToDeleteBulk(false);
        if (isExportModalOpen) setIsExportModalOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isModalOpen,
    isPending,
    isSourceModalOpen,
    isCreatingSource,
    isAssignModalOpen,
    isImportModalOpen,
    contactToDelete,
    contactsToDeleteBulk,
    isDeletingBatch,
    isExportModalOpen,
  ]);

  const handleEditClick = async (contact) => {
    if (!contact?.id) return;
    // The row/card object can be missing full phone metadata — fetch the
    // full contact so the edit form's country code parses correctly
    // (see phoneUtils.parsePhoneForForm).
    try {
      const fullContact = await queryClient.fetchQuery({
        queryKey: ['crm-contact-detail', currentCompanyId, contact.id],
        queryFn: () =>
          getCRMContactDetail({
            companyId: currentCompanyId,
            contactId: contact.id,
          }),
      });
      setSelectedContact(fullContact);
    } catch {
      setSelectedContact(contact);
    }
    setIsModalOpen(true);
  };

  const handleViewClick = (contact) => {
    if (!contact?.id) return;
    const contactId = String(contact.id);
    const contactName = contact.name || '';
    try {
      sessionStorage.setItem('crm-contact-overview-id', contactId);
      sessionStorage.setItem('crm-contact-overview-name', contactName);
      window.dispatchEvent(new Event('crm-contact-overview-updated'));
    } catch {
      // ignore storage errors
    }
    // Original shareable URLs (currently hidden from address bar via state/sessionStorage)
    // const encodedContactId = encodeURIComponent(encodeId(contactId));
    // console.log('[Leads deep-link URLs] from contacts list', {
    //   contactId,
    //   raw: `${window.location.origin}/crm-contacts/overview?contact_id=${contactId}`,
    //   encoded: `${window.location.origin}/crm-contacts/overview?contact_id=${encodedContactId}`,
    //   pathOnlyRaw: `/crm-contacts/overview?contact_id=${contactId}`,
    //   pathOnlyEncoded: `/crm-contacts/overview?contact_id=${encodedContactId}`,
    // });
    navigate('/crm-contacts/overview', {
      state: {
        contactId,
        contactName,
        from: location.pathname + location.search,
      },
    });
  };

  const hasLeadActions =
    can(PERMISSIONS.LEADS.UPDATE) || can(PERMISSIONS.LEADS.DELETE);

  const leadColumns = [
    {
      id: 'select',
      label: '',
      defaultWidth: 48,
      minWidth: 48,
      resizable: true,
      align: 'center',
      stopPropagation: true,
      skeleton: <SkeletonText width="w-4" height="h-4" className="mx-auto" />,
      header: () => (
        <V2Checkbox
          checked={headerChecked(filteredContacts)}
          onChange={handleToggleSelectAll}
          isHeader
        />
      ),
      cell: (contact) => {
        const isDeleted =
          contact.name === 'Deleted Contact' ||
          contact.is_deleted ||
          !!contact.deleted_at;
        return (
          <V2Checkbox
            checked={isSelected(contact.id)}
            onChange={() => toggleSelection(contact.id)}
            disabled={isDeleted}
          />
        );
      },
    },
    {
      id: 'sr',
      label: 'Sr No.',
      defaultWidth: 72,
      minWidth: 56,
      resizable: true,
      skeleton: <SkeletonText width="w-7" height="h-3.5" />,
      cell: (_contact, index) => (
        <span className="truncate block text-xs font-medium text-[#64748B]">
          {(page - 1) * LEADS_PAGE_SIZE + index + 1}
        </span>
      ),
    },
    {
      id: 'name',
      label: 'Name',
      defaultWidth: 220,
      minWidth: 140,
      resizable: true,
      skeleton: <SkeletonText width="w-32" height="h-4" />,
      cell: (contact) => {
        const isNewContact = contact?.is_new === true;
        return (
          <div className="flex items-center gap-2 overflow-hidden">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleViewClick(contact);
              }}
              className="truncate text-sm font-medium text-primary hover:underline focus:outline-none"
              title={contact.name || '-'}
            >
              {contact.name || '-'}
            </button>
            {isNewContact && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary/20 text-primary border border-primary/30 whitespace-nowrap">
                New
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: 'phone',
      label: 'Phone',
      defaultWidth: 160,
      minWidth: 120,
      resizable: true,
      skeleton: <SkeletonText width="w-28" height="h-4" />,
      cell: (contact) => {
        const phone = getDisplayPhone(contact);
        return (
          <span className="truncate block text-sm text-[#1E293B]" title={phone}>
            {phone}
          </span>
        );
      },
    },
    {
      id: 'email',
      label: 'Email',
      defaultWidth: 240,
      minWidth: 140,
      resizable: true,
      skeleton: <SkeletonText width="w-40" height="h-4" />,
      cell: (contact) => (
        <span
          className={`truncate block text-sm ${contact.email ? 'text-[#1E293B]' : 'text-text-muted'}`}
          title={contact.email || '-'}
        >
          {contact.email || '-'}
        </span>
      ),
    },
    {
      id: 'consent',
      label: 'Consent',
      defaultWidth: 130,
      minWidth: 100,
      resizable: true,
      skeleton: (
        <SkeletonText width="w-16" height="h-5" className="!rounded-full" />
      ),
      cell: (contact) => (
        <TableChip tone={getConsentChipTone(contact.consent_status)}>
          {contact.consent_status
            ? formatConsentStatus(contact.consent_status)
            : 'Unknown'}
        </TableChip>
      ),
    },
    {
      id: 'createdAt',
      label: 'Created Date',
      defaultWidth: 180,
      minWidth: 130,
      resizable: true,
      skeleton: <SkeletonText width="w-36" height="h-4" />,
      cell: (contact) => {
        const label = formatDate(contact.created_at);
        return (
          <span className="truncate block text-xs text-[#64748B]" title={label}>
            {label}
          </span>
        );
      },
    },
    ...(hasLeadActions
      ? [
          {
            id: 'actions',
            label: 'Action',
            defaultWidth: 100,
            minWidth: 80,
            resizable: true,
            align: 'center',
            stopPropagation: true,
            skeleton: (
              <div className="flex items-center justify-center gap-2">
                <SkeletonText
                  width="w-6"
                  height="h-6"
                  className="!rounded-md"
                />
                <SkeletonText
                  width="w-6"
                  height="h-6"
                  className="!rounded-md"
                />
              </div>
            ),
            cell: (contact) => {
              const isDeleted =
                contact.name === 'Deleted Contact' ||
                contact.is_deleted ||
                !!contact.deleted_at;
              return (
                <div className="flex items-center justify-center gap-1.5">
                  <PermissionGuard permission={PERMISSIONS.LEADS.UPDATE}>
                    <button
                      type="button"
                      onClick={() => handleEditClick(contact)}
                      className="p-1.5 hover:bg-[#F1F5F9] rounded-lg transition-colors focus:outline-none shrink-0"
                      title="Edit Lead"
                    >
                      <img
                        src={editIcon}
                        alt="Edit"
                        className="w-4 h-4 object-contain shrink-0"
                      />
                    </button>
                  </PermissionGuard>
                  <PermissionGuard permission={PERMISSIONS.LEADS.DELETE}>
                    <button
                      type="button"
                      onClick={() => handleDelete(contact.id)}
                      className={`p-1.5 rounded-lg transition-colors focus:outline-none shrink-0 ${isDeleted ? 'opacity-30 cursor-not-allowed' : 'hover:bg-[#FEEFEF]'}`}
                      title={
                        isDeleted ? 'Cannot delete deleted lead' : 'Delete Lead'
                      }
                      disabled={isDeleted}
                    >
                      <img
                        src={trashIcon}
                        alt="Delete"
                        className="w-4 h-4 object-contain shrink-0"
                      />
                    </button>
                  </PermissionGuard>
                </div>
              );
            },
          },
        ]
      : []),
  ];

  const consentFilterOptions = [
    { value: 'opt_in', label: 'Opt In' },
    { value: 'opt_out', label: 'Opt Out' },
    { value: 'unknown', label: 'Unknown' },
  ];

  return (
    <DashboardPage className="contacts-page leads-page !bg-transparent !p-0 h-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="w-full h-full min-h-0 flex flex-col gap-4 rounded-[16px] bg-white border border-[#EBF4EA] p-4 sm:p-5 pb-4 overflow-hidden">
        {/* Toolbar: search + Import/Export/Filter left, Create Lead right */}
        <div className="flex flex-col gap-3 w-full shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 flex-1 min-w-0">
              <SearchInput
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search leads by name, phone, or email"
                className="w-full sm:max-w-md sm:flex-1"
                inputClassName="h-[44px]"
              />

              <div className="flex flex-nowrap items-center gap-2 sm:gap-3 w-full sm:w-auto shrink-0">
                <div
                  className="relative shrink-0 flex-1 sm:flex-initial"
                  ref={filterMenuRef}
                >
                  <button
                    type="button"
                    onClick={() => setIsFilterMenuOpen((prev) => !prev)}
                    aria-expanded={isFilterMenuOpen}
                    aria-haspopup="menu"
                    className={`relative h-[44px] w-full px-2.5 sm:px-4 rounded-[12px] text-xs sm:text-[15px] font-semibold flex items-center justify-center gap-1.5 sm:gap-2 transition-colors focus:outline-none ${
                      isFilterMenuOpen || activeFilterFields.length > 0
                        ? 'bg-primary text-white hover:bg-primary-hover'
                        : 'bg-[#F5F5F5] text-[#1F2241] hover:brightness-95'
                    }`}
                  >
                    <FilterIcon className="w-4 h-4 shrink-0" />
                    Filter
                  </button>
                  {isFilterMenuOpen && (
                    <div
                      role="menu"
                      data-overlay-container="true"
                      style={{ overscrollBehavior: 'contain' }}
                      className="absolute left-0 top-[52px] z-40 w-56 bg-white border border-[#EBF4EA] rounded-xl shadow-xl py-2"
                    >
                      <p className="px-4 py-1.5 text-xs font-semibold text-text-muted uppercase tracking-wide">
                        Filter by
                      </p>
                      {FILTER_FIELDS.filter(
                        (f) => !activeFilterFields.includes(f.key),
                      ).map((f) => (
                        <button
                          key={f.key}
                          type="button"
                          role="menuitem"
                          onClick={() => handleAddFilterField(f.key)}
                          className="w-full text-left px-4 py-2 text-sm font-medium text-[#1F2241] hover:bg-[#EBF4EA] hover:text-[#50AB3D] transition-colors"
                        >
                          {f.label}
                        </button>
                      ))}
                      {activeFilterFields.length === FILTER_FIELDS.length && (
                        <p className="px-4 py-2 text-sm text-text-muted">
                          All filters added
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {can(PERMISSIONS.LEADS.CREATE) && (
                  <button
                    type="button"
                    onClick={() => setIsImportModalOpen(true)}
                    className="inline-flex h-[44px] flex-1 sm:flex-initial items-center justify-center gap-1 sm:gap-[6px] rounded-[12px] px-2.5 sm:px-4 text-xs sm:text-[15px] font-medium font-sf-pro-display text-[#1F2241] transition-colors focus:outline-none hover:brightness-95"
                    style={{ backgroundColor: '#F5F5F5' }}
                  >
                    Import
                    <img
                      src={importIcon}
                      alt=""
                      className="w-5 h-5 sm:w-6 sm:h-6 object-contain shrink-0"
                    />
                  </button>
                )}
                {can(PERMISSIONS.LEADS.VIEW) && (
                  <button
                    type="button"
                    onClick={onExportClick}
                    disabled={isExporting}
                    className="inline-flex h-[44px] flex-1 sm:flex-initial items-center justify-center gap-1 sm:gap-[6px] rounded-[12px] px-2.5 sm:px-4 text-xs sm:text-[15px] font-medium font-sf-pro-display text-[#1F2241] transition-colors focus:outline-none hover:brightness-95 disabled:opacity-70 disabled:cursor-not-allowed"
                    style={{ backgroundColor: '#F5F5F5' }}
                  >
                    {isExporting ? 'Exporting...' : 'Export'}
                    <img
                      src={exportIcon}
                      alt=""
                      className="w-5 h-5 sm:w-6 sm:h-6 object-contain shrink-0"
                    />
                  </button>
                )}

                {selectedCount > 0 && can(PERMISSIONS.LEADS.DELETE) && (
                  <button
                    type="button"
                    onClick={() => setContactsToDeleteBulk(true)}
                    className="h-[44px] px-4 rounded-[12px] bg-[#FEEFEF] text-[#F04438] hover:bg-[#FDE2E2] transition-colors inline-flex items-center gap-2.5 font-semibold text-sm cursor-pointer border border-[#FECDCA]/60 animate-fadeIn shrink-0"
                    title="Delete Selected Leads"
                  >
                    <span>{selectedCount} item(s) selected</span>
                    <svg
                      className="w-5 h-5 stroke-[2] stroke-[#F04438]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {can(PERMISSIONS.LEADS.CREATE) && (
              <Button
                variant="primary"
                onClick={() => {
                  setSelectedContact(null);
                  setIsModalOpen(true);
                }}
                className="h-[44px] px-4 w-full sm:w-auto shrink-0 sm:ml-auto"
                icon={<PlusIcon className="w-4 h-4 sm:w-5 sm:h-5" />}
              >
                <span className="whitespace-nowrap">Create Lead</span>
              </Button>
            )}
          </div>

          {activeFilterFields.length > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              {activeFilterFields.map((key) => {
                const field = FILTER_FIELDS.find((f) => f.key === key);
                if (!field) return null;
                return (
                  <div
                    key={key}
                    className="h-10 flex items-center gap-2 pl-3 pr-2 rounded-xl border border-[#EBF4EA] bg-white shrink-0"
                  >
                    <span className="text-sm font-semibold text-[#1F2241] whitespace-nowrap">
                      {field.label}
                    </span>
                    <span className="text-sm text-[#868686]">is</span>

                    {key === 'stage' && (
                      <CustomSelect
                        value={stageFilter}
                        onChange={setStageFilter}
                        options={[
                          { value: '', label: 'All Stages' },
                          ...stageOptions,
                        ]}
                        dropdownWidth={180}
                        buttonClassName="h-8 px-2 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center gap-1 cursor-pointer focus:outline-none"
                        textSizeClass="text-sm"
                      />
                    )}
                    {key === 'source' && (
                      <CustomSelect
                        value={sourceFilter}
                        onChange={setSourceFilter}
                        options={[
                          { value: '', label: 'All Sources' },
                          ...sourceOptions,
                        ]}
                        dropdownWidth={160}
                        buttonClassName="h-8 px-2 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center gap-1 cursor-pointer focus:outline-none"
                        textSizeClass="text-sm"
                      />
                    )}
                    {key === 'tag' && (
                      <CustomSelect
                        value={tagFilter}
                        onChange={setTagFilter}
                        options={[
                          { value: '', label: 'All Tags' },
                          ...tagOptions,
                        ]}
                        searchable
                        onSearchChange={tagsList.setSearch}
                        onLoadMore={tagsList.loadMore}
                        hasMore={tagsList.hasMore}
                        isFetchingMore={tagsList.isFetchingMore}
                        dropdownWidth={160}
                        buttonClassName="h-8 px-2 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center gap-1 cursor-pointer focus:outline-none"
                        textSizeClass="text-sm"
                      />
                    )}
                    {key === 'createDate' && (
                      <CustomDateRangePicker
                        startDate={dateFrom}
                        endDate={dateTo}
                        onApply={(start, end) => {
                          setDateFrom(start);
                          setDateTo(end);
                        }}
                        onClear={() => {
                          setDateFrom('');
                          setDateTo('');
                        }}
                        triggerClassName="h-8 px-2 rounded-lg border-0 bg-[#F5F5F5] text-sm justify-between"
                        iconSrc={datePickerCalendarIcon}
                      />
                    )}
                    {key === 'estimatedValue' && (
                      <div className="flex items-center gap-1 h-8 px-2 rounded-lg bg-[#F5F5F5]">
                        <input
                          type="number"
                          value={valueMin}
                          onChange={(e) => setValueMin(e.target.value)}
                          placeholder="Min"
                          className="w-14 text-sm text-[#1F2241] placeholder:text-text-muted bg-transparent border-0 focus:outline-none"
                        />
                        <span className="text-[#868686] text-xs shrink-0">
                          -
                        </span>
                        <input
                          type="number"
                          value={valueMax}
                          onChange={(e) => setValueMax(e.target.value)}
                          placeholder="Max"
                          className="w-14 text-sm text-[#1F2241] placeholder:text-text-muted bg-transparent border-0 focus:outline-none"
                        />
                      </div>
                    )}
                    {key === 'assigned' && (
                      <CustomSelect
                        value={assignedFilter}
                        onChange={setAssignedFilter}
                        options={[
                          { value: '', label: 'All Employees' },
                          ...employeeOptions,
                        ]}
                        searchable
                        onSearchChange={employeesList.setSearch}
                        onLoadMore={employeesList.loadMore}
                        hasMore={employeesList.hasMore}
                        isFetchingMore={employeesList.isFetchingMore}
                        dropdownWidth={200}
                        buttonClassName="h-8 px-2 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center gap-1 cursor-pointer focus:outline-none"
                        textSizeClass="text-sm"
                      />
                    )}
                    {key === 'consent' && (
                      <CustomSelect
                        value={consentFilter}
                        onChange={setConsentFilter}
                        options={[
                          { value: '', label: 'All' },
                          ...consentFilterOptions,
                        ]}
                        dropdownWidth={140}
                        buttonClassName="h-8 px-2 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center gap-1 cursor-pointer focus:outline-none"
                        textSizeClass="text-sm"
                      />
                    )}
                    {key === 'automation' && (
                      <CustomSelect
                        value={flowFilter}
                        onChange={setFlowFilter}
                        options={[
                          { value: '', label: 'All Stage Triggers' },
                          ...flowOptions,
                        ]}
                        searchable
                        onSearchChange={flowsList.setSearch}
                        onLoadMore={flowsList.loadMore}
                        hasMore={flowsList.hasMore}
                        isFetchingMore={flowsList.isFetchingMore}
                        dropdownWidth={200}
                        buttonClassName="h-8 px-2 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center gap-1 cursor-pointer focus:outline-none"
                        textSizeClass="text-sm"
                      />
                    )}
                    {key === 'customField' && (
                      <div className="flex items-center gap-1">
                        <CustomSelect
                          value={customFieldSlug}
                          onChange={(val) => {
                            setCustomFieldSlug(val);
                            setCustomFieldValue('');
                            setDebouncedCustomFieldValue('');
                          }}
                          options={[
                            { value: '', label: 'Select field' },
                            ...customFieldOptions,
                          ]}
                          searchable
                          onSearchChange={customFieldsList.setSearch}
                          onLoadMore={customFieldsList.loadMore}
                          hasMore={customFieldsList.hasMore}
                          isFetchingMore={customFieldsList.isFetchingMore}
                          dropdownWidth={160}
                          buttonClassName="h-8 px-2 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center gap-1 cursor-pointer focus:outline-none"
                          textSizeClass="text-sm"
                        />
                        {!customFieldSlug ? (
                          <input
                            type="text"
                            value=""
                            disabled
                            placeholder="Value"
                            className="h-8 w-28 px-2 rounded-lg bg-[#F5F5F5] text-sm text-[#1F2241] placeholder:text-text-muted border-0 focus:outline-none opacity-50 cursor-not-allowed"
                          />
                        ) : selectedCustomField?.field_type === 'select' ? (
                          <CustomSelect
                            value={customFieldValue}
                            onChange={setCustomFieldValue}
                            options={[
                              { value: '', label: 'Select value' },
                              ...(Array.isArray(selectedCustomField.options)
                                ? selectedCustomField.options
                                : String(selectedCustomField.options || '')
                                    .split(',')
                                    .map((o) => o.trim())
                                    .filter(Boolean)
                              ).map((opt) => ({
                                value: opt,
                                label: opt,
                              })),
                            ]}
                            dropdownWidth={140}
                            buttonClassName="h-8 px-2 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center gap-1 cursor-pointer focus:outline-none"
                            textSizeClass="text-sm"
                          />
                        ) : selectedCustomField?.field_type === 'boolean' ? (
                          <CustomSelect
                            value={customFieldValue}
                            onChange={setCustomFieldValue}
                            options={[
                              { value: '', label: 'Any' },
                              { value: 'true', label: 'Yes' },
                              { value: 'false', label: 'No' },
                            ]}
                            dropdownWidth={100}
                            buttonClassName="h-8 px-2 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center gap-1 cursor-pointer focus:outline-none"
                            textSizeClass="text-sm"
                          />
                        ) : (
                          <input
                            type={
                              selectedCustomField?.field_type === 'number'
                                ? 'number'
                                : selectedCustomField?.field_type === 'date'
                                  ? 'date'
                                  : 'text'
                            }
                            value={customFieldValue}
                            onChange={(e) =>
                              setCustomFieldValue(e.target.value)
                            }
                            placeholder="Value"
                            className="h-8 w-28 px-2 rounded-lg bg-[#F5F5F5] text-sm text-[#1F2241] placeholder:text-text-muted border-0 focus:outline-none"
                          />
                        )}
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => handleRemoveFilterField(key)}
                      aria-label={`Remove ${field.label} filter`}
                      className="text-[#868686] hover:text-[#1F2241] shrink-0"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                );
              })}

              <button
                type="button"
                onClick={handleClearAllFilters}
                className="text-sm font-semibold text-[#50AB3D] hover:text-primary-hover shrink-0"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        <div className="w-full min-w-0 flex-1 min-h-0 flex flex-col overflow-hidden">
          <V2Table
            columns={leadColumns}
            data={isContactsError && !contactsData ? [] : filteredContacts}
            rowKey="id"
            rowId={(contact) => `contact-row-${contact.id}`}
            storageKey="crm_leads_table_column_widths"
            loading={isLoading || (!contactsData && !isContactsError)}
            loadingRowCount={LEADS_PAGE_SIZE}
            notice={
              isContactsError && !contactsData
                ? parseApiError(contactsError)?.message ||
                  'Failed to load leads'
                : null
            }
            empty={
              <div className="flex flex-col items-center justify-center gap-3">
                <div className="w-14 h-14 bg-[#F8FAFC] border border-[#E2E8F0] rounded-full flex items-center justify-center">
                  <svg
                    className="w-7 h-7 text-[#94A3B8]"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-[#475569]">
                  No leads found
                </span>
              </div>
            }
            onRowClick={handleViewClick}
            isRowSelected={(contact) => isSelected(contact.id)}
            rowClassName={(contact) => {
              if (highlightedContactId === contact.id) {
                return 'bg-[#EBF4EA]/70 border-l-4 border-l-[#50AB3D] animate-notification-pulse';
              }
              if (contact?.is_new === true) {
                return 'bg-primary/5 border-l-2 border-l-primary';
              }
              return '';
            }}
            pagination={{
              currentPage: page,
              totalCount: count,
              itemsPerPage: LEADS_PAGE_SIZE,
              onChange: setPage,
            }}
          />
        </div>
      </div>

      {/* Create / Update Contact Drawer */}
      <SideDrawer
        isOpen={isModalOpen}
        onClose={() => {
          if (!isPending) setIsModalOpen(false);
        }}
        width="xl"
        className="lead-form-drawer"
        zIndexClass="z-[60]"
        showClose={false}
        bodyClassName="p-0 flex flex-col min-h-0 !overflow-hidden"
        headerClassName="!px-6 !py-5"
        header={
          <div className="flex items-center justify-between w-full gap-3">
            <h2 className="text-[20px] leading-[28px] font-semibold text-[#1F2241] font-sf-pro-display">
              {selectedContact ? 'Update Lead' : 'Create Lead'}
            </h2>
            <button
              type="button"
              onClick={() => {
                if (!isPending) setIsModalOpen(false);
              }}
              className="w-8 h-8 rounded-[8px] bg-primary text-white flex items-center justify-center hover:bg-primary-hover transition-colors focus:outline-none shrink-0"
              aria-label="Close"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.75"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        }
      >
        <CRMContactForm
          companyId={currentCompanyId}
          entity="lead"
          initialContact={selectedContact}
          isPending={isPending}
          apiErrors={apiErrors}
          onSubmit={(payload) => {
            const { source, ...contactPayload } = payload;
            if (selectedContact) {
              handleUpdateContact(
                {
                  companyId: currentCompanyId,
                  contactId: selectedContact.id,
                  payload: contactPayload,
                },
                {
                  onSuccess: () => {
                    if (source) {
                      handleCreateSource({
                        companyId: currentCompanyId,
                        contactId: selectedContact.id,
                        payload: source,
                      });
                    }
                  },
                },
              );
            } else {
              handleCreateContact(
                {
                  companyId: currentCompanyId,
                  payload: contactPayload,
                },
                {
                  onSuccess: (data) => {
                    const newContactId =
                      data?.id ||
                      data?.contact?.id ||
                      data?.results?.id ||
                      data?.data?.id;
                    if (!newContactId) return;

                    if (source) {
                      handleCreateSource({
                        companyId: currentCompanyId,
                        contactId: newContactId,
                        payload: source,
                      });
                    }
                  },
                },
              );
            }
          }}
        />
      </SideDrawer>

      <ConfirmDeleteModal
        isOpen={!!contactToDelete}
        title="Delete Lead"
        description="This lead and their information will be permanently deleted."
        onCancel={() => setContactToDelete(null)}
        onConfirm={() => {
          executeDeleteContact({
            companyId: currentCompanyId,
            contactId: contactToDelete,
          });
          setContactToDelete(null);
        }}
      />

      <ConfirmDeleteModal
        isOpen={contactsToDeleteBulk}
        title="Delete Selected Leads"
        description={`Are you sure you want to delete ${selectedCount} selected lead(s)? This action cannot be undone.`}
        onCancel={() => setContactsToDeleteBulk(false)}
        onConfirm={() => executeBatchDelete()}
        isPending={isDeletingBatch}
      />

      {/* Import Wizard */}
      <CRMImportWizard
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        companyId={currentCompanyId}
        onImportSuccess={() => {
          queryClient.invalidateQueries({
            queryKey: ['crm-contacts', currentCompanyId],
          });
        }}
      />

      {/* Export Modal */}
      <CRMExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={onExportSubmit}
        isExporting={isExporting}
        filteredCount={count}
        hasActiveFilters={Object.keys(buildListParams()).length > 0}
        companyId={currentCompanyId}
        getExportFilterParams={() =>
          buildCRMContactsExportFilterParams(buildListParams(), { tagIdByName })
        }
      />
    </DashboardPage>
  );
}
